import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Target } from "lucide-react";
import { OPP_PRODUCTS, OPP_STAGES, OPP_CLOSED, PRIORITIES, LOST_REASONS, LEAD_ORIGINS } from "@/lib/crm";

const EMPTY = {
  product: "Fotovoltaico", stage: "Nuovo lead", priority: "Media", lead_origin: "",
  estimated_value: "", next_action: "", next_action_date: "", lost_reason: "", notes: "",
};

// customerId required for create; opportunity for edit
export default function OpportunityDialog({ open, onOpenChange, customerId, opportunity, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const editing = !!opportunity;
  const [f, setF] = useState(EMPTY);
  const [operators, setOperators] = useState([]);
  const [consultantId, setConsultantId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setF(opportunity ? {
      product: opportunity.product, stage: opportunity.stage, priority: opportunity.priority || "Media",
      lead_origin: opportunity.lead_origin || "", estimated_value: opportunity.estimated_value ?? "",
      next_action: opportunity.next_action || "", next_action_date: opportunity.next_action_date || "",
      lost_reason: opportunity.lost_reason || "", notes: opportunity.notes || "",
    } : { ...EMPTY });
    setConsultantId(opportunity?.consultant_id || "");
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [open, opportunity, isAdmin]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isClosed = OPP_CLOSED.includes(f.stage);

  const save = async () => {
    setError(""); setSaving(true);
    try {
      const payload = {
        ...f, customer_id: customerId,
        estimated_value: f.estimated_value === "" ? null : Number(f.estimated_value),
        next_action_date: f.next_action_date || null,
        lead_origin: f.lead_origin || null, lost_reason: f.lost_reason || null,
      };
      if (isAdmin && consultantId) payload.consultant_id = consultantId;
      const { data } = editing
        ? await api.put(`/opportunities/${opportunity.id}`, payload)
        : await api.post(`/opportunities`, payload);
      toast.success(editing ? "Opportunità aggiornata" : "Opportunità creata");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-xl" data-testid="opportunity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Target className="h-5 w-5 text-brand" /> {editing ? "Modifica opportunità" : "Nuova opportunità"}</DialogTitle>
          <DialogDescription>Efficienza energetica. La prossima azione è obbligatoria finché l'opportunità è aperta.</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="opportunity-error">{error}</div>}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Prodotto</Label>
              <Select value={f.product} onValueChange={(v) => set("product", v)}>
                <SelectTrigger data-testid="opp-product"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{OPP_PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Fase pipeline</Label>
              <Select value={f.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger data-testid="opp-stage"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{OPP_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Priorità</Label>
              <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="opp-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Valore stimato (€)</Label><Input type="number" step="0.01" value={f.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} data-testid="opp-value" /></div>
          </div>

          {!isClosed ? (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-brand/25 bg-brand/5 p-3">
              <div className="space-y-1.5"><Label className="text-brand">Prossima azione *</Label><Input value={f.next_action} onChange={(e) => set("next_action", e.target.value)} placeholder="Es. Chiamare per sopralluogo" data-testid="opp-next-action" /></div>
              <div className="space-y-1.5"><Label className="text-brand">Data prossima azione *</Label><Input type="date" value={f.next_action_date} onChange={(e) => set("next_action_date", e.target.value)} data-testid="opp-next-date" /></div>
            </div>
          ) : f.stage === "Persa" ? (
            <div className="space-y-1.5"><Label>Motivo perdita *</Label>
              <Select value={f.lost_reason || "none"} onValueChange={(v) => set("lost_reason", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="opp-lost-reason"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{LOST_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Origine</Label>
              <Select value={f.lead_origin || "none"} onValueChange={(v) => set("lead_origin", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="opp-origin"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{LEAD_ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div className="space-y-1.5"><Label>Consulente</Label>
                <Select value={consultantId || "none"} onValueChange={(v) => setConsultantId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="opp-consultant"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-1.5"><Label>Note</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="opp-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="opp-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
